/**
 * @file routes/_app/audit-tasks/$taskId.index.tsx
 * @description 稽核任務詳情頁（`/audit-tasks/:taskId/` 的 index leaf）。
 *
 * 命名說明：同目錄有 `$taskId.form.$instanceId.tsx`（表單填寫子路由）。
 * 本檔走 `$taskId.index.tsx` 形態，作為 `/audit-tasks/$taskId/` 的 index leaf，
 * 與 form 子路由並列、互不嵌套。若改回 `$taskId.tsx`，TanStack Router 會將其視為
 * layout route，但本詳情頁未渲染 `<Outlet />`，會把 form 子路由整個屏蔽。
 *
 * 結構：
 *  - PageShell：標題（任務 ID / 案件 / 客戶 / 狀態 Pill）+ 返回列表按鈕
 *  - PhaseSteps：頂部 6 階段導航（開案準備 → 計劃 → 現場 → 檢驗 → 報告 → 結案）
 *  - 階段內容區：根據 selectedPhase 切換顯示對應 Phase 組件
 *
 * 數據來源：GET /api/audit-tasks/:id（含 case 摘要、teammates、forms、推斷的 phase）
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeft, ListChecks, LogOut, Wifi, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AUDIT_TASK_PHASES } from '@zhcb/shared';
import type { AuditTaskDetail, AuditTaskPhase, AuditTaskStatus } from '@zhcb/shared';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';
import { PageShell, StatusPill, type StatusPillProps } from '@/components/shell';
import { GlobalSyncBadge } from '@/components/audit-tasks/GlobalSyncBadge';
import { TaskPackButton } from '@/components/audit-tasks/TaskPackButton';
import { PhaseSteps } from '@/components/audit-tasks/PhaseSteps';
import { PhasePreparation } from '@/components/audit-tasks/phases/PhasePreparation';
import { PhasePlanning } from '@/components/audit-tasks/phases/PhasePlanning';
import { PhaseField } from '@/components/audit-tasks/phases/PhaseField';
import { parseFieldTab, type FieldTab } from '@/components/audit-tasks/phases/field-tabs';
import { PhaseLab } from '@/components/audit-tasks/phases/PhaseLab';
import { PhaseReport } from '@/components/audit-tasks/phases/PhaseReport';
import { PhaseClosing } from '@/components/audit-tasks/phases/PhaseClosing';
import { setOfflineFocusIntent, useOfflineFocusMode } from '@/hooks/useOfflineFocusMode';

export const Route = createFileRoute('/_app/audit-tasks/$taskId/')({
  component: AuditTaskDetailPage,
  /**
   * `?phase=` 接受全部 6 個階段值（非法值一律折疊為 undefined）：
   *  - `field` 仍保留「離線現場入口」語義（見 useOfflineFocusMode 與 CLAUDE.md §10）；
   *  - 其餘階段值作為進頁面時的初始定位（表單填寫頁「返回任務」帶回來源階段用），
   *    使用者仍可透過頂部 Steps 自由切換。
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { phase?: AuditTaskPhase; tab?: FieldTab } => ({
    phase: AUDIT_TASK_PHASES.includes(search.phase as AuditTaskPhase)
      ? (search.phase as AuditTaskPhase)
      : undefined,
    // 序21：現場稽核初始頁籤（表單填寫頁「返回任務」帶回來源頁籤，如 稽核文件包）
    tab: parseFieldTab(search.tab),
  }),
});

/**
 * 將稽核完成期限（ISO 字串）格式化為「XXXX年XX月XX日前」文案（客戶 PPT S47）。
 * 未設定期限（null）回 null，由呼叫端決定是否渲染。
 */
function formatAuditDeadlineText(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}年${mm}月${dd}日前`;
}

const STATUS_TONE: Record<AuditTaskStatus, StatusPillProps['tone']> = {
  待派案: 'muted',
  已派案: 'info',
  已確認: 'brand',
  計畫中: 'brand',
  現場中: 'warning',
  報告中: 'info',
  完成: 'success',
  退回改派: 'danger',
};

function AuditTaskDetailPage() {
  const navigate = useNavigate();
  const { taskId } = Route.useParams();
  const search = Route.useSearch();
  const qc = useQueryClient();
  const { isOnline, focusMode } = useOfflineFocusMode();

  const { data, isLoading, isError } = useQuery<{ task: AuditTaskDetail }>({
    queryKey: ['audit-task-detail', Number(taskId)],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ task: AuditTaskDetail }>(
        `/api/audit-tasks/${taskId}`,
        { taskId: Number(taskId), scope: 'task-detail' },
      );
      return res.data;
    },
  });

  const task = data?.task;
  // selectedPhase：本地切換顯示。預設定位優先級：
  //  1. 使用者點擊 Steps 的選擇（selectedPhase）
  //  2. URL `?phase=` 帶入的初始階段（表單填寫頁「返回任務」會帶回來源階段）
  //  3. 第一步「開案準備」——不跟隨後端推斷的 task.phase，由使用者自行點擊 Steps 切換。
  const [selectedPhase, setSelectedPhase] = useState<AuditTaskPhase | null>(null);
  const forceFieldPhase = focusMode || !isOnline || search.phase === 'field';
  const effectivePhase: AuditTaskPhase = forceFieldPhase
    ? 'field'
    : (selectedPhase ?? search.phase ?? 'preparation');

  // PageShell actions：全域同步徽章 + 返回列表按鈕。
  // error 與 success 分支共用同一份；loading 分支不顯示 actions。
  const exitFocusMode = () => {
    setOfflineFocusIntent(false);
    window.location.assign(`/audit-tasks/${taskId}`);
  };

  const focusActions = (
    <Space wrap>
      {/* inline-flex 包住圖標+文字，避免 Tailwind preflight 的 svg{display:block} 使裸 lucide 圖標佔整行而文字換行 */}
      <Tag color={isOnline ? 'green' : 'orange'}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {isOnline ? <Wifi size={12} /> : <WifiOff size={12} />}
          {isOnline ? '線上' : '離線'}
        </span>
      </Tag>
      <Button
        size="small"
        icon={<ListChecks size={14} />}
        onClick={() => window.location.assign('/me/offline')}
      >
        離線任務
      </Button>
      {isOnline && (
        <Button size="small" icon={<LogOut size={14} />} onClick={exitFocusMode}>
          返回完整後台
        </Button>
      )}
    </Space>
  );

  const normalActions = (
    <Space>
      <GlobalSyncBadge />
      <TaskPackButton taskId={Number(taskId)} />
      <Button onClick={() => void navigate({ to: '/audit-tasks' })} icon={<ArrowLeft size={14} />}>
        返回列表
      </Button>
    </Space>
  );

  const headerActions = focusMode || !isOnline ? focusActions : normalActions;

  if (isLoading) {
    return (
      <PageShell title="稽核任務詳情">
        <Card>
          <Typography.Text>載入中…</Typography.Text>
        </Card>
      </PageShell>
    );
  }

  if (isError || !task) {
    return (
      <PageShell title="稽核任務詳情" actions={headerActions}>
        <Alert type="error" message="任務不存在或無權限訪問" />
      </PageShell>
    );
  }

  const triggerRefetch = () => {
    void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
  };

  return (
    <PageShell
      title={
        <Space size={12} align="center">
          <span style={{ fontFamily: 'monospace' }}>AUD-#{task.id}</span>
          {/* 序77（2026-09-25）：標題旁顯示客戶「驗證註冊碼」（如 ZH-CAS0564），
              不再顯示內部案件編號（2026-CAS0564-01）；無註冊碼時才回落案件編號，
              案件編號保留在滑鼠提示中供對照。 */}
          {(task.clientCode || task.caseCode) && (
            <Tooltip title={task.caseCode ? `案件編號：${task.caseCode}` : undefined}>
              <Typography.Text type="secondary" style={{ fontSize: 14, fontFamily: 'monospace' }}>
                · {task.clientCode || task.caseCode}
              </Typography.Text>
            </Tooltip>
          )}
          <StatusPill tone={STATUS_TONE[task.status] ?? 'muted'} dot>
            {task.status}
          </StatusPill>
          {task.isTeamLead && <Tag color="gold">組長任務</Tag>}
          {/* 稽核計劃編號（客戶 PPT S47）：派案時整案一號、同組任務共用；舊任務無編號不顯示 */}
          {task.planCode && (
            <Tag color="blue" style={{ fontFamily: 'monospace' }}>
              {task.planCode}
            </Tag>
          )}
        </Space>
      }
      description={
        // 客戶 / 方案 / 驗證類型 + 稽核完成期限（客戶 PPT S47，未指定不顯示）
        [
          task.clientName
            ? `${task.clientName}${task.caseScheme ? ` · ${task.caseScheme}` : ''}${task.caseVerificationType ? ` · ${task.caseVerificationType}` : ''}`
            : null,
          formatAuditDeadlineText(task.caseAuditDeadline)
            ? `稽核完成期限：${formatAuditDeadlineText(task.caseAuditDeadline)}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ') || undefined
      }
      actions={headerActions}
      contentGap={!focusMode && isOnline ? undefined : 16}
    >
      {/**
       * Pad/手機螢幕較窄時，6 階段 Steps 會超出寬度；
       * 用 overflow-x: auto 讓使用者橫向滑動，比強制換行更自然。
       */}
      {!focusMode && isOnline && (
        <Card
          style={{ marginBottom: 24 }}
          size="small"
          styles={{ body: { overflowX: 'auto', WebkitOverflowScrolling: 'touch' } }}
        >
          <div style={{ minWidth: 720 }}>
            <PhaseSteps
              currentPhase={task.phase}
              selectedPhase={effectivePhase}
              labWaived={task.labWaivedAt != null}
              onSelect={setSelectedPhase}
            />
          </div>
        </Card>
      )}

      <div>
        {effectivePhase === 'preparation' && <PhasePreparation task={task} />}
        {effectivePhase === 'planning' && <PhasePlanning task={task} onChanged={triggerRefetch} />}
        {effectivePhase === 'field' && <PhaseField task={task} initialTab={search.tab} />}
        {effectivePhase === 'lab' && <PhaseLab task={task} />}
        {effectivePhase === 'report' && <PhaseReport task={task} />}
        {effectivePhase === 'closing' && <PhaseClosing task={task} />}
      </div>
    </PageShell>
  );
}
